import React from "react";
import {
  Delete as DeleteIcon,
  DragHorizontalVariant as DragHorizontalVariantIcon,
  Plus as PlusIcon,
} from "@actinc/dls/icons/";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  ControlledFormEditor,
  ControlledFormSwitch,
} from "../../../../components";

import { useDraggableInPortal } from "../../../../services/hooks";
import { getLetter, onDragEnd } from "../../../../services/utils";

const InlineChoiceInteractionForm = ({ index, interaction, readonly }) => {
  const namePrefix = `item.interactions[${index}]`;
  // console.debug(
  //   "InlineChoiceInteractionForm - index %s, interaction %o",
  //   index,
  //   interaction,
  // );

  const renderDraggable = useDraggableInPortal();

  const { control, getValues, watch } = useFormContext();
  watch(`${namePrefix}.choices`);

  const {
    append,
    fields: choices,
    move,
    remove,
  } = useFieldArray({
    control,
    name: `${namePrefix}.choices`,
  });

  const addChoice = () => {
    append({
      correct: false,
      text: "<p></p>",
    });
  };
  const deleteChoice = (i) => () => {
    remove(i);
  };
  const disableCorrectSwitches = (i) => (e) => {
    if (getValues(`${namePrefix}.choices`).findIndex((o) => o.correct) === -1)
      return;

    if (
      e.target.checked &&
      e.target.checked !== getValues(`${namePrefix}.choices[${i}].correct`)
    ) {
      e.target.checked = false;
      e.stopPropagation();
    }
  };

  if (!getValues(`${namePrefix}.identifier`)) return null;

  return (
    <Box
      sx={{
        alignItems: "self-start",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
      }}
    >
      <DragDropContext onDragEnd={onDragEnd(move)}>
        <Droppable droppableId={`${interaction.identifier}-choices`}>
          {(droppableProvided) => (
            <List
              dense={true}
              disablePadding={true}
              ref={droppableProvided.innerRef}
              sx={{ width: "100%" }}
              {...droppableProvided.droppableProps}
            >
              {!!choices.length &&
                choices.map((choice, i) => {
                  const letter = getLetter(i);
                  return (
                    <Draggable
                      draggableId={choice.id}
                      index={i}
                      isDragDisabled={readonly}
                      key={choice.id}
                    >
                      {renderDraggable((draggableProvided) => (
                        <ListItem
                          dense={true}
                          disableGutters={true}
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                        >
                          <Box
                            sx={{
                              backgroundColor: "background.paper",
                              flexDirection: "column",
                              flexGrow: 1,
                            }}
                          >
                            <Box
                              sx={{
                                alignItems: "center",
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Box
                                sx={{
                                  alignItems: "center",
                                  display: "flex",
                                  flexDirection: "row",
                                }}
                              >
                                <Box {...draggableProvided.dragHandleProps}>
                                  <DragHorizontalVariantIcon />
                                </Box>
                                <Typography
                                  sx={{ display: "inline-flex" }}
                                  variant="subtitle1"
                                >{`Choice ${letter}`}</Typography>
                              </Box>
                              <ControlledFormSwitch
                                control={control}
                                defaultValue={choice.correct}
                                id={`${namePrefix}.choices[${i}].correct`}
                                label="Correct"
                                name={`${namePrefix}.choices[${i}].correct`}
                                onChange={disableCorrectSwitches(i)}
                                readonly={readonly}
                              />
                              {!readonly && (
                                <Tooltip title="Delete Choice">
                                  <IconButton
                                    aria-label={`delete choice ${letter}`}
                                    disabled={
                                      getValues(`${namePrefix}.choices`)
                                        .length <= 2
                                    }
                                    onClick={deleteChoice(i)}
                                    size="large"
                                    sx={{ padding: 1 }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <ControlledFormEditor
                              control={control}
                              id={`${namePrefix}.choices[${i}].text`}
                              name={`${namePrefix}.choices[${i}].text`}
                              onReady={(ed) => {
                                console.info(
                                  `Item Interaction ${namePrefix} choice ${i} editor is ready, %o`,
                                  ed,
                                );
                              }}
                              options={["inlineChoice"]}
                              readonly={readonly}
                            />
                          </Box>
                        </ListItem>
                      ))}
                    </Draggable>
                  );
                })}
              {droppableProvided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
      {!readonly && (
        <Button
          onClick={addChoice}
          size="small"
          startIcon={<PlusIcon />}
          variant="outlined"
        >
          Create Choice
        </Button>
      )}
      <ControlledFormEditor
        control={control}
        defaultValue={interaction.distractorRationale}
        getValues={getValues}
        id={`${namePrefix}.distractorRationale`}
        label="Distractor Rationale"
        name={`${namePrefix}.distractorRationale`}
        onReady={(ed) => {
          console.info(
            `Item Interaction ${namePrefix} distractorRationale editor is ready, %o`,
            ed,
          );
        }}
        readonly={readonly}
      />
    </Box>
  );
};

export { InlineChoiceInteractionForm };
